import { Component, Input, OnInit, inject } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { AsyncPipe } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgSelectModule } from "@ng-select/ng-select";
import { KriDataService } from "@app/kri/services/kri-data.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { getFormControl, getFormValue, setFormControlValue } from "@methods/CommonMethods";
import { KeyValue } from "@models/generic/KeyValuePair";
import { KriModel } from "@models/kri";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { CerrixPromptComponent } from "@app/shared/cerrix-prompt/cerrix-prompt.component";
import { tap } from "rxjs/operators";
import _ from "underscore";
import { firstValueFrom } from "rxjs";
import { CerrixTextfieldComponent } from "@app/shared/components/cerrix-textinput/cerrix-textfield/cerrix-textfield.component";
import { CerrixSelectTreeComponent } from "@app/shared/cerrix-select-tree/cerrix-select-tree.component";
import { CerrixTextAreaComponent } from "@app/shared/components/cerrix-textinput/cerrix-textarea/cerrix-textarea.component";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";

@Component({
    selector: "kri-detail-main",
    templateUrl: "./kri-detail-main.component.html",
    styleUrls: ["./kri-detail-main.component.scss"],
    imports: [
        ReactiveFormsModule,
        AsyncPipe,
        MatTooltipModule,
        NgSelectModule,
        CerrixTextfieldComponent,
        CerrixSelectTreeComponent,
        CerrixTextAreaComponent,
        CheckboxComponent,
    ],
})
export class KriDetailMainComponent implements OnInit {
    private _kriDS = inject(KriDataService);
    private _promptService = inject(CerrixPromptService);

    @Input() organizations: CerrixTreeItem[];
    @Input() businessDimensions: CerrixTreeItem[];
    @Input() frameworkDimensions: CerrixTreeItem[];
    @Input() kriModel: KriModel;
    @Input() mainFormGroup: FormGroup;

    loadingData = false;

    riskCataloguesLoading = true;
    frequenciesLoading = true;
    aggregationsLoading = true;
    graphTypesLoading = true;
    measurementsLoading = true;

    riskCatalogues: Promise<KeyValue<number, string>[]>;
    frequencies: Promise<KeyValue<number, string>[]>;
    aggregations: Promise<KeyValue<number, string>[]>;
    graphTypes: Promise<KeyValue<number, string>[]>;
    measurements: Promise<KeyValue<string, string>[]>;

    private originalOrganizationIds: number[] = [];
    private previousSelectedBusinessDimensionIds: number[] = [];

    get organizationIds(): number[] {
        return getFormValue<KriModel>(this.mainFormGroup, "organizationIds");
    }
    get businessDimensionIds(): number[] {
        return getFormValue<KriModel>(this.mainFormGroup, "businessDimensionIds");
    }
    get frameworkDimensionIds(): number[] {
        return getFormValue<KriModel>(this.mainFormGroup, "frameworkDimensionIds");
    }
    get riskCatalogueIds(): number[] {
        return getFormValue<KriModel>(this.mainFormGroup, "riskCatalogueIds");
    }

    ngOnInit() {
        const organizationIds = this.organizationIds;
        const riskCatalogueIds = this.riskCatalogueIds;
        this.originalOrganizationIds = organizationIds;
        this.previousSelectedBusinessDimensionIds = this.businessDimensionIds;

        this.riskCatalogues = firstValueFrom(
            this._kriDS
                .getRiskCatalogues(organizationIds, riskCatalogueIds)
                .pipe(tap(() => (this.riskCataloguesLoading = false))),
        );

        this.frequencies = firstValueFrom(
            this._kriDS.getFrequencies().pipe(tap(() => (this.frequenciesLoading = false))),
        );

        this.aggregations = firstValueFrom(
            this._kriDS.getAggregations().pipe(tap(() => (this.aggregationsLoading = false))),
        );

        this.graphTypes = firstValueFrom(
            this._kriDS.getGraphTypes().pipe(tap(() => (this.graphTypesLoading = false))),
        );

        this.measurements = firstValueFrom(
            this._kriDS.getMeasurements().pipe(tap(() => (this.measurementsLoading = false))),
        );
        if (!this.organizationIds || organizationIds.length === 0) {
            getFormControl<KriModel>(this.mainFormGroup, "businessDimensionIds").disable();
            getFormControl<KriModel>(this.mainFormGroup, "frameworkDimensionIds").disable();
            getFormControl<KriModel>(this.mainFormGroup, "riskCatalogueIds").disable();
        }
    }

    async organizationsChanged() {
        if (_.isEqual(_.sortBy(this.organizationIds), _.sortBy(this.originalOrganizationIds))) {
            return;
        }

        if (
            this.businessDimensionIds.length > 0 ||
            this.frameworkDimensionIds.length > 0 ||
            this.riskCatalogueIds.length > 0
        ) {
            const confirmed = await this._promptService
                .confirm(
                    "Organization change",
                    "Selected Business Dimensions, Framework Dimensions, and Risk Catalogues will be reset. Change organizations anyway?",
                )
                .toPromise();

            if (!confirmed) {
                getFormControl<KriModel>(this.mainFormGroup, "organizationIds").setValue(
                    this.originalOrganizationIds,
                );
                return;
            }
        }
        const riskCatalogueIdsControl = getFormControl<KriModel>(
            this.mainFormGroup,
            "riskCatalogueIds",
        );
        const businessDimensionIdsControl = getFormControl<KriModel>(
            this.mainFormGroup,
            "businessDimensionIds",
        );
        const frameworkDimensionIdsControl = getFormControl<KriModel>(
            this.mainFormGroup,
            "frameworkDimensionIds",
        );

        this.originalOrganizationIds = this.organizationIds;
        this.kriModel.organizationIds = this.organizationIds;
        businessDimensionIdsControl.setValue([]);
        frameworkDimensionIdsControl.setValue([]);
        riskCatalogueIdsControl.setValue([]);

        if (!this.organizationIds || this.organizationIds.length === 0) {
            this.businessDimensions = [];
            this.frameworkDimensions = [];
            this.riskCatalogues = null;
            businessDimensionIdsControl.disable();
            frameworkDimensionIdsControl.disable();
            riskCatalogueIdsControl.disable();
            return;
        }

        this._kriDS
            .getBusinessDimensions(this.organizationIds, this.businessDimensionIds)
            .subscribe((businessDimensions) => {
                this.businessDimensions = businessDimensions;
                businessDimensionIdsControl.enable();
            });
        this._kriDS.getFrameworkDimensions(this.kriModel).subscribe((frameworkDimensions) => {
            this.frameworkDimensions = frameworkDimensions;
            frameworkDimensionIdsControl.enable();
        });

        riskCatalogueIdsControl.enable();
        this.riskCataloguesLoading = true;
        this.riskCatalogues = firstValueFrom(
            this._kriDS
                .getRiskCatalogues(this.organizationIds, this.riskCatalogueIds)
                .pipe(tap(() => (this.riskCataloguesLoading = false))),
        );
    }

    async onBusinessDimensionChange(bds: number[]): Promise<void> {
        if (!bds || !bds.isDifferent(this.kriModel.businessDimensionIds)) {
            return;
        }
        this.kriModel.businessDimensionIds = this.businessDimensionIds;
        this.kriModel.frameworkDimensionIds = this.frameworkDimensionIds;

        const msg = await firstValueFrom(this._kriDS.validateBusinessDimensionChange(this.kriModel));
        if (!msg || msg.messages.length == 0) {
            this.reloadFrameWorkDimensions(bds);
            return;
        }

        //currently only FD validation, leaving the structure I stole from control to make future expansion easier
        let message = "These items can't be deleted, ";
        const hasInvalidFrameworkDimensions = msg.invalidFrameworkDimensions?.length > 0;

        let linksDetailMessage: string;
        if (hasInvalidFrameworkDimensions) {
            linksDetailMessage = "framework dimensions. ";
        }
        message +=
            "due to links with " +
            linksDetailMessage +
            "\nDo you want to change the business dimensions and remove the following item(s)? :\n\n";
        msg.messages.forEach((m) => (message = message + m + "\n"));

        const confirmed = await firstValueFrom(
            this.createConfirm("Cannot change business dimensions!", message).getResult(),
        );
        if (confirmed) {
            this.updateFrameworkDimension(msg.invalidFrameworkDimensions);
            this.reloadFrameWorkDimensions(bds);
        } else {
            this.kriModel.businessDimensionIds = this.previousSelectedBusinessDimensionIds;
            setFormControlValue<KriModel>(
                this.mainFormGroup,
                "businessDimensionIds",
                this.previousSelectedBusinessDimensionIds,
            );
        }
    }

    reloadFrameWorkDimensions(bds: number[]): void {
        this.previousSelectedBusinessDimensionIds = bds;
        this.kriModel.businessDimensionIds = this.businessDimensionIds;
        const formControl = getFormControl<KriModel>(this.mainFormGroup, "frameworkDimensionIds");
        const previousValues = formControl.value;

        formControl.disable();
        this._kriDS.getFrameworkDimensions(this.kriModel).subscribe((frameworkDimensions) => {
            this.frameworkDimensions = frameworkDimensions;
            formControl.setValue(previousValues);
            formControl.enable();
        });
    }

    updateFrameworkDimension(invalidFd: number[]): void {
        const previousFd = this.kriModel.frameworkDimensionIds;
        for (const ifd of invalidFd) {
            const index = previousFd.indexOf(ifd);
            if (index !== -1) {
                previousFd.splice(index, 1);
            }
        }
        this.kriModel.frameworkDimensionIds = previousFd;
    }

    private createConfirm(title: string, body: string): CerrixPromptComponent {
        return this._promptService.confirmCustom({
            maxWidth: "450px",
            maxHeight: "350px",
            data: {
                title: title,
                message: body,
            },
        });
    }
}
